import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import api from "../api/client";
import { toast } from "sonner";
import {
  Image as ImageIcon, Box, Video, Upload, Trash2, Star, Loader2,
  X, Eye,
} from "lucide-react";
import Model3DViewer from "./Model3DViewer";
import VideoPlayer from "./VideoPlayer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Dialog admin pour gérer toutes les médias d'une unité :
 *   - photos (cover + galerie)
 *   - modèles 3D (.glb / .gltf)
 *   - vidéos (mp4 / webm / mov)
 */
export default function UnitMediaDialog({ open, onClose, unite }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("photos");
  const [preview, setPreview] = useState(null); // { kind, url, content_type, caption }
  const [uploading, setUploading] = useState(false);

  const photoRef = useRef(null);
  const modelRef = useRef(null);
  const videoRef = useRef(null);

  const load = async () => {
    if (!unite?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get("/media", { params: { owner_type: "unite", owner_id: unite.id } });
      setItems(data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) { setTab("photos"); load(); } }, [open, unite?.id]); // eslint-disable-line

  const upload = async (file, kind) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("owner_type", "unite");
      fd.append("owner_id", unite.id);
      fd.append("kind", kind);
      fd.append("file", file);
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Média ajouté");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec d'envoi");
    } finally {
      setUploading(false);
    }
  };

  const setCover = async (id) => {
    try {
      await api.patch(`/media/${id}`, { kind: "cover" });
      toast.success("Couverture définie");
      await load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce média ?")) return;
    try {
      await api.delete(`/media/${id}`);
      toast.success("Supprimé");
      await load();
    } catch { toast.error("Erreur"); }
  };

  const photos = items.filter((m) => ["cover", "gallery"].includes(m.kind));
  const models = items.filter((m) => m.kind === "model3d");
  const videos = items.filter((m) => m.kind === "video");

  const tabs = [
    { k: "photos", label: "Photos", icon: ImageIcon, count: photos.length },
    { k: "model3d", label: "Maquette 3D", icon: Box, count: models.length },
    { k: "video", label: "Vidéos", icon: Video, count: videos.length },
  ];

  if (!unite) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
        <DialogContent className="bg-white max-w-4xl p-0 overflow-hidden max-h-[92vh] flex flex-col" data-testid="unit-media-dialog">
          <DialogHeader className="px-6 pt-5 pb-4 border-b border-[#E2E5F1] shrink-0">
            <DialogTitle className="font-display text-xl text-[#0F1A3D]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Médias · {unite.nom}
            </DialogTitle>
            <div className="text-xs text-[#4B5775] mt-1">Ajoutez photos, maquette 3D interactive (.glb/.gltf) et vidéos (drone, visite).</div>

            {/* Tabs */}
            <div className="flex items-center gap-1 mt-4 border-b border-[#E2E5F1] -mb-4">
              {tabs.map((t) => {
                const Ic = t.icon;
                const active = tab === t.k;
                return (
                  <button
                    key={t.k}
                    type="button"
                    onClick={() => setTab(t.k)}
                    className={`inline-flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition ${active ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#4B5775] hover:text-[#0F1A3D]"}`}
                    data-testid={`umd-tab-${t.k}`}
                  >
                    <Ic className="w-4 h-4" /> {t.label}
                    <span className="bg-[#F1F3FB] text-[#4B5775] text-[10px] px-1.5 rounded">{t.count}</span>
                  </button>
                );
              })}
            </div>
          </DialogHeader>

          <div className="px-6 py-5 overflow-y-auto flex-1">
            {loading ? (
              <div className="text-center py-10 text-[#4B5775] text-sm"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Chargement…</div>
            ) : (
              <>
                {/* === PHOTOS === */}
                {tab === "photos" && (
                  <div>
                    <UploadBar
                      label="JPG / PNG / WEBP — max 8 Mo"
                      onPick={() => photoRef.current?.click()}
                      uploading={uploading}
                      testid="umd-upload-photo"
                    />
                    <input ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => upload(e.target.files?.[0], "gallery")} />
                    {photos.length === 0 ? (
                      <Empty msg="Aucune photo pour cette unité." />
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {photos.map((m) => (
                          <div key={m.id} className="relative group rounded-lg overflow-hidden border border-[#E2E5F1] bg-[#F8FAFC]" data-testid={`umd-photo-${m.id}`}>
                            <img src={`${BACKEND_URL}/api/public/media/${m.id}`} alt={m.caption || "Photo"} className="w-full h-32 object-cover" />
                            {m.kind === "cover" && (
                              <div className="absolute top-1.5 left-1.5 bg-[#E91E63] text-white text-[9px] uppercase font-bold px-1.5 py-0.5 rounded">Couverture</div>
                            )}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                              {m.kind !== "cover" && (
                                <button onClick={() => setCover(m.id)} className="w-8 h-8 bg-white rounded-md inline-flex items-center justify-center hover:bg-[#1E3A8A] hover:text-white" title="Définir comme couverture">
                                  <Star className="w-4 h-4" />
                                </button>
                              )}
                              <button onClick={() => remove(m.id)} className="w-8 h-8 bg-white rounded-md inline-flex items-center justify-center hover:bg-[#C83B3B] hover:text-white" title="Supprimer">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* === MODEL 3D === */}
                {tab === "model3d" && (
                  <div>
                    <UploadBar
                      label=".glb ou .gltf — max 40 Mo · format glTF 2.0 recommandé"
                      onPick={() => modelRef.current?.click()}
                      uploading={uploading}
                      testid="umd-upload-model"
                    />
                    <input ref={modelRef} type="file" accept=".glb,.gltf,model/gltf-binary,model/gltf+json" hidden onChange={(e) => upload(e.target.files?.[0], "model3d")} />
                    {models.length === 0 ? (
                      <Empty msg="Aucune maquette 3D pour cette unité." sub="Importez un fichier .glb (binaire glTF) pour permettre une visite 3D interactive sur le site." />
                    ) : (
                      <div className="space-y-3">
                        {models.map((m) => (
                          <div key={m.id} className="border border-[#E2E5F1] rounded-lg p-3 flex items-center gap-3" data-testid={`umd-model-${m.id}`}>
                            <Box className="w-10 h-10 text-[#1E3A8A] shrink-0" strokeWidth={1.5} />
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-sm text-[#0F1A3D] truncate">Maquette 3D #{m.id.slice(0, 8)}</div>
                              <div className="text-[11px] text-[#4B5775] mt-0.5">{m.content_type} · {(m.size / 1024 / 1024).toFixed(1)} Mo</div>
                            </div>
                            <button onClick={() => setPreview({ kind: "model3d", url: `/api/public/media/${m.id}`, caption: m.caption })} className="h-9 px-3 rounded-md border border-[#E2E5F1] hover:border-[#1E3A8A] text-xs font-bold text-[#1E3A8A] inline-flex items-center gap-1.5">
                              <Eye className="w-3.5 h-3.5" /> Aperçu
                            </button>
                            <button onClick={() => remove(m.id)} className="w-9 h-9 rounded-md border border-[#E2E5F1] hover:border-[#C83B3B] hover:text-[#C83B3B] text-[#4B5775] inline-flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* === VIDEO === */}
                {tab === "video" && (
                  <div>
                    <UploadBar
                      label="MP4 / WEBM / MOV — max 100 Mo"
                      onPick={() => videoRef.current?.click()}
                      uploading={uploading}
                      testid="umd-upload-video"
                    />
                    <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov" hidden onChange={(e) => upload(e.target.files?.[0], "video")} />
                    {videos.length === 0 ? (
                      <Empty msg="Aucune vidéo pour cette unité." sub="Drone, visite filmée, présentation du quartier… toute vidéo aide à convaincre." />
                    ) : (
                      <div className="space-y-3">
                        {videos.map((m) => (
                          <div key={m.id} className="border border-[#E2E5F1] rounded-lg p-3 flex items-center gap-3" data-testid={`umd-video-${m.id}`}>
                            <Video className="w-10 h-10 text-[#E91E63] shrink-0" strokeWidth={1.5} />
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-sm text-[#0F1A3D] truncate">Vidéo #{m.id.slice(0, 8)}</div>
                              <div className="text-[11px] text-[#4B5775] mt-0.5">{m.content_type} · {(m.size / 1024 / 1024).toFixed(1)} Mo</div>
                            </div>
                            <button onClick={() => setPreview({ kind: "video", url: `/api/public/media/${m.id}`, content_type: m.content_type, caption: m.caption })} className="h-9 px-3 rounded-md border border-[#E2E5F1] hover:border-[#E91E63] text-xs font-bold text-[#E91E63] inline-flex items-center gap-1.5">
                              <Eye className="w-3.5 h-3.5" /> Aperçu
                            </button>
                            <button onClick={() => remove(m.id)} className="w-9 h-9 rounded-md border border-[#E2E5F1] hover:border-[#C83B3B] hover:text-[#C83B3B] text-[#4B5775] inline-flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="umd-preview">
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPreview(null)} className="ml-auto mb-2 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
            {preview.kind === "model3d" ? (
              <Model3DViewer src={preview.url} caption={preview.caption} height="70vh" />
            ) : (
              <VideoPlayer src={preview.url} caption={preview.caption} contentType={preview.content_type || "video/mp4"} height="70vh" />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function UploadBar({ label, onPick, uploading, testid }) {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={uploading}
      className="w-full mb-4 border-2 border-dashed border-[#E2E5F1] hover:border-[#1E3A8A] hover:bg-[#E8EBFA] rounded-lg p-5 text-center transition disabled:opacity-50"
      data-testid={testid}
    >
      {uploading ? (
        <Loader2 className="w-6 h-6 text-[#1E3A8A] animate-spin mx-auto" />
      ) : (
        <Upload className="w-6 h-6 text-[#1E3A8A] mx-auto" />
      )}
      <div className="text-sm font-bold text-[#0F1A3D] mt-2">Cliquez ou glissez un fichier</div>
      <div className="text-[11px] text-[#4B5775] mt-0.5">{label}</div>
    </button>
  );
}

function Empty({ msg, sub }) {
  return (
    <div className="text-center py-8 px-4 bg-[#F8FAFC] border border-dashed border-[#E2E5F1] rounded-lg">
      <div className="text-sm font-bold text-[#0F1A3D]">{msg}</div>
      {sub && <div className="text-xs text-[#4B5775] mt-1">{sub}</div>}
    </div>
  );
}
